import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Papa from "papaparse";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {
  ListChecks, Camera, QrCode, MagnifyingGlass, CheckCircle, Warning, Package,
  ArrowRight, ArrowLeft, DownloadSimple, ClipboardText, Scan as ScanIcon, XCircle, Cube,
} from "@phosphor-icons/react";

const READER_ID = "stock-take-reader";
const STEPS = ["Scan rolls", "Not scanned", "Per-unit count", "Summary"];

function beep(ok = true) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = ok ? 880 : 300;
    g.gain.value = 0.05;
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, ok ? 90 : 180);
  } catch { /* no-op */ }
}

function extractRollId(text) {
  const m = String(text).match(/\/rolls\/([0-9a-fA-F-]{36})/);
  if (m) return m[1];
  if (/^[0-9a-fA-F-]{36}$/.test(String(text).trim())) return String(text).trim();
  return null;
}

export default function StockTake() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ active_rolls: [], per_unit: [] });

  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [scanOrder, setScanOrder] = useState([]);      // roll objects, newest first
  const [unexpected, setUnexpected] = useState([]);    // {code,label,status,reason}
  const [depleteIds, setDepleteIds] = useState(new Set());
  const [counts, setCounts] = useState({});            // product_id -> string
  const [applying, setApplying] = useState(false);
  const [summary, setSummary] = useState(null);

  // Refs the (async) scanner callback reads without stale closures
  const scannerRef = useRef(null);
  const scannedRef = useRef(new Set());        // roll ids
  const unexpectedRef = useRef(new Map());     // code -> obj
  const recentRef = useRef({});                // code -> ts (debounce)
  const activeByIdRef = useRef({});
  const activeBySkuRef = useRef({});

  useEffect(() => {
    api.get("/stock-take/data")
      .then(({ data }) => {
        setData(data);
        activeByIdRef.current = Object.fromEntries((data.active_rolls || []).map((r) => [r.id, r]));
        activeBySkuRef.current = Object.fromEntries((data.active_rolls || []).map((r) => [r.sku, r]));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load stock take data"))
      .finally(() => setLoading(false));
  }, []);

  const stopScanner = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) { try { await s.stop(); } catch {} try { await s.clear(); } catch {} }
    setScanning(false);
  };
  useEffect(() => () => { stopScanner(); }, []);

  const total = data.active_rolls.length;
  const scannedCount = scanOrder.length;

  const addPresent = (rollObj) => {
    scannedRef.current.add(rollObj.id);
    setScanOrder((prev) => [rollObj, ...prev]);
    beep(true);
    toast.success(rollObj.product_name, { description: `${rollObj.sku} · ${scannedRef.current.size} of ${total} found` });
  };

  const addUnexpected = (obj) => {
    if (!unexpectedRef.current.has(obj.code)) {
      unexpectedRef.current.set(obj.code, obj);
      setUnexpected((prev) => [obj, ...prev]);
    }
    beep(false);
  };

  const handleCode = async (raw) => {
    const text = String(raw || "").trim();
    if (!text) return;
    const now = Date.now();
    if (recentRef.current[text] && now - recentRef.current[text] < 2500) return;
    recentRef.current[text] = now;

    // Fast path: match against the pre-loaded active rolls (QR roll-id URL or exact SKU).
    const rid = extractRollId(text);
    const fast = (rid && activeByIdRef.current[rid]) || activeBySkuRef.current[text] || null;
    if (fast) {
      if (scannedRef.current.has(fast.id)) {
        toast.warning("Already scanned", { description: `${fast.product_name} · ${fast.sku}` });
        return;
      }
      addPresent(fast);
      return;
    }

    // Authoritative resolution via the same lookup the Scan page uses (handles labels
    // whose encoded value isn't an exact match to our pre-loaded active list).
    let d;
    try {
      ({ data: d } = await api.get("/rolls/lookup", { params: { code: text } }));
    } catch {
      addUnexpected({ code: text, label: text, status: "unknown", reason: "Unrecognised label — not in the system" });
      toast.error("Label not recognised", { description: "This code isn't in the system." });
      return;
    }
    const resolved = d?.roll;
    if (!resolved) {
      addUnexpected({ code: text, label: text, status: "unknown", reason: "Unrecognised label — not in the system" });
      toast.error("Label not recognised", { description: "This code isn't in the system." });
      return;
    }
    if (scannedRef.current.has(resolved.id)) {
      toast.warning("Already scanned", { description: `${d.product?.name || ""} · ${resolved.sku || ""}`.trim() });
      return;
    }
    // Active roll that should be on the shelf -> mark present (even if it wasn't in the
    // initially-loaded set, e.g. added since the page opened).
    if (activeByIdRef.current[resolved.id] || resolved.status === "active") {
      const display = activeByIdRef.current[resolved.id] || {
        id: resolved.id,
        sku: resolved.sku || "",
        batch_number: resolved.batch_number || "",
        current_length_m: resolved.current_length_m || 0,
        location: resolved.location || "",
        product_name: d.product?.name || "—",
        manufacturer: d.manufacturer?.name || "",
        series: d.series?.name || "",
        color_hex: d.product?.color_hex,
      };
      if (!activeByIdRef.current[resolved.id]) {
        activeByIdRef.current[resolved.id] = display;
        if (display.sku) activeBySkuRef.current[display.sku] = display;
        setData((prev) => ({ ...prev, active_rolls: [...prev.active_rolls, display] }));
      }
      addPresent(display);
      return;
    }
    // Genuinely unexpected: incoming / depleted / archived.
    const reason = resolved.status === "incoming" ? "Incoming stock — not active yet"
      : resolved.status === "depleted" ? "Already marked depleted / archived"
      : `Not active (status: ${resolved.status || "unknown"})`;
    addUnexpected({ code: text, label: `${d.product?.name || ""} · ${resolved.sku || ""}`.trim(), status: resolved.status, reason });
    toast.warning("Not added to the list", { description: reason });
  };

  const startScan = async () => {
    setScanning(true);
    try {
      const html5 = new Html5Qrcode(READER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.CODE_128],
        verbose: false,
      });
      scannerRef.current = html5;
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => { handleCode(decodedText); },  // continuous — do NOT stop
        () => {},
      );
    } catch {
      setScanning(false);
      toast.error("Could not start camera. Use manual entry, or check camera permissions.");
    }
  };

  const doManual = () => {
    const code = manual.trim();
    if (code) { handleCode(code); setManual(""); }
  };

  const scannedIds = useMemo(() => new Set(scanOrder.map((r) => r.id)), [scanOrder]);
  const presentRolls = useMemo(() => data.active_rolls.filter((r) => scannedIds.has(r.id)), [data.active_rolls, scannedIds]);
  const missingRolls = useMemo(() => data.active_rolls.filter((r) => !scannedIds.has(r.id)), [data.active_rolls, scannedIds]);

  const toggleDeplete = (id) => setDepleteIds((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllDeplete = () => setDepleteIds((prev) =>
    prev.size === missingRolls.length ? new Set() : new Set(missingRolls.map((r) => r.id)));

  const goReview = async () => { await stopScanner(); setStep(1); };

  const perUnitDiff = (p) => {
    const v = counts[p.id];
    if (v === "" || v == null) return null;
    return Number(v) - p.current_count;
  };

  const apply = async () => {
    setApplying(true);
    try {
      const per_unit_counts = Object.entries(counts)
        .filter(([, v]) => v !== "" && v != null && !Number.isNaN(Number(v)))
        .map(([product_id, v]) => ({ product_id, counted: Math.max(0, Math.round(Number(v))) }));
      const payload = {
        scanned_count: scannedCount,
        present_count: presentRolls.length,
        missing_count: missingRolls.length,
        deplete_roll_ids: [...depleteIds],
        per_unit_counts,
      };
      const { data: res } = await api.post("/stock-take/apply", payload);
      setSummary(res);
      setStep(3);
      if ((res.pending_consume_count || 0) > 0) toast.success(`Stock take done — ${res.pending_consume_count} roll${res.pending_consume_count === 1 ? "" : "s"} sent for admin approval`);
      else toast.success("Stock take completed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setApplying(false); }
  };

  const downloadReport = () => {
    const rows = [];
    presentRolls.forEach((r) => rows.push({ category: "Roll", result: "Present (scanned)", product: r.product_name, brand: r.manufacturer, series: r.series, sku_or_code: r.sku, batch: r.batch_number, length_m: r.current_length_m, location: r.location }));
    missingRolls.forEach((r) => rows.push({ category: "Roll", result: depleteIds.has(r.id) ? (isAdmin ? "Not scanned → marked consumed" : "Not scanned → sent for admin approval") : "Not scanned → kept", product: r.product_name, brand: r.manufacturer, series: r.series, sku_or_code: r.sku, batch: r.batch_number, length_m: r.current_length_m, location: r.location }));
    unexpected.forEach((u) => rows.push({ category: "Unexpected scan", result: u.reason, product: u.label, brand: "", series: "", sku_or_code: u.code, batch: "", length_m: "", location: "" }));
    data.per_unit.forEach((p) => {
      const v = counts[p.id];
      if (v === "" || v == null) return;
      rows.push({ category: "Per-unit", result: `system ${p.current_count} → counted ${Number(v)}`, product: p.name, brand: p.manufacturer, series: p.series, sku_or_code: p.base_sku, batch: "", length_m: "", location: p.location });
    });
    const csv = Papa.unparse({ fields: ["category", "result", "product", "brand", "series", "sku_or_code", "batch", "length_m", "location"], data: rows });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vwa-stock-take-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const resetAll = () => {
    scannedRef.current = new Set(); unexpectedRef.current = new Map(); recentRef.current = {};
    setScanOrder([]); setUnexpected([]); setDepleteIds(new Set()); setCounts({}); setSummary(null); setStep(0);
  };

  if (loading) {
    return <div className="p-8 text-sm text-slate-500" data-testid="stock-take-loading">Loading stock take…</div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-[900px]" data-testid="stock-take-page">
      <header className="mb-6">
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Inventory · Reconcile</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <ListChecks size={30} /> Stock Take
        </h1>
        <p className="text-sm text-slate-600 mt-2">Scan every roll on the shelf, then hand-count per-unit goods. The app reconciles against your inventory and flags anything not found.</p>
      </header>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="stock-take-stepper">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${i === step ? "bg-slate-900 text-white border-slate-900" : i < step ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-400 border-slate-200"}`}>
              <span className="font-mono">{i + 1}</span> {label}
            </div>
            {i < STEPS.length - 1 && <ArrowRight size={14} className="text-slate-300" />}
          </div>
        ))}
      </div>

      {/* STEP 1 — SCAN */}
      {step === 0 && (
        <div className="space-y-5" data-testid="step-scan">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="border border-slate-200 bg-white rounded-md p-4">
              {!scanning && (
                <div className="w-full aspect-square max-w-[260px] mx-auto mb-4 border-2 border-dashed border-slate-200 rounded-xl grid place-items-center bg-slate-50">
                  <div className="text-center px-4"><QrCode size={44} className="mx-auto text-slate-300" /><div className="text-xs text-slate-400 mt-2">Point the camera at each roll label — keep scanning, it stays on</div></div>
                </div>
              )}
              <div id={READER_ID} className={`w-full mx-auto max-w-[320px] ${scanning ? "mb-4" : "hidden"} rounded-md overflow-hidden`} />
              {!scanning
                ? <Button onClick={startScan} className="w-full h-11 bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="start-scan-btn"><Camera size={18} /> Start camera</Button>
                : <Button onClick={stopScanner} variant="outline" className="w-full h-11 gap-2 rounded-md" data-testid="stop-scan-btn">Pause camera</Button>}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Or enter a SKU manually</Label>
                <div className="flex gap-2 mt-2">
                  <div className="relative flex-1 min-w-0">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doManual()} placeholder="TWVW…" className="pl-9 h-11 font-mono" data-testid="manual-code-input" />
                  </div>
                  <Button onClick={doManual} disabled={!manual.trim()} className="h-11 shrink-0 bg-slate-900 hover:bg-slate-800 rounded-md" data-testid="manual-add-btn">Add</Button>
                </div>
              </div>
            </div>

            <div className="border border-slate-200 bg-white rounded-md p-4 flex flex-col">
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600 flex items-center gap-1.5"><ScanIcon size={14} /> Found</div>
                <div className="font-mono text-sm" data-testid="scan-progress"><span className="text-2xl font-semibold text-emerald-600 tabular-nums">{scannedCount}</span> <span className="text-slate-400">/ {total}</span></div>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${total ? (scannedCount / total) * 100 : 0}%` }} />
              </div>
              <div className="flex-1 overflow-y-auto max-h-72 -mx-1 px-1 space-y-1" data-testid="scan-feed">
                {scanOrder.length === 0 && <div className="text-xs text-slate-400 text-center py-8">Scanned rolls will appear here.</div>}
                {scanOrder.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs border border-slate-100 rounded-md px-2 py-1.5">
                    <CheckCircle weight="fill" size={16} className="text-emerald-500 shrink-0" />
                    <span className="font-medium truncate">{r.product_name}</span>
                    <span className="font-mono text-slate-400 ml-auto shrink-0">{r.sku}</span>
                  </div>
                ))}
              </div>
              {unexpected.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid="unexpected-list">
                  <div className="text-[10px] font-mono uppercase tracking-wide text-amber-700 mb-1.5 flex items-center gap-1"><Warning size={12} /> {unexpected.length} unexpected scan{unexpected.length === 1 ? "" : "s"}</div>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {unexpected.map((u, i) => (
                      <div key={i} className="text-[11px] text-slate-600 truncate"><span className="font-mono">{u.label || u.code}</span> — <span className="text-amber-700">{u.reason}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={goReview} className="bg-slate-900 hover:bg-slate-800 gap-2 rounded-md" data-testid="finish-scan-btn">
              Review not scanned <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2 — REVIEW NOT SCANNED */}
      {step === 1 && (
        <div className="space-y-5" data-testid="step-review">
          <div className="grid grid-cols-3 gap-0 border border-slate-200 rounded-md overflow-hidden bg-white">
            <div className="p-4 border-r border-slate-200"><div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Present</div><div className="font-mono text-3xl font-semibold text-emerald-600 tabular-nums" data-testid="present-count">{presentRolls.length}</div></div>
            <div className="p-4 border-r border-slate-200"><div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Not scanned</div><div className="font-mono text-3xl font-semibold text-amber-600 tabular-nums" data-testid="missing-count">{missingRolls.length}</div></div>
            <div className="p-4"><div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">{isAdmin ? "To mark consumed" : "To send for approval"}</div><div className="font-mono text-3xl font-semibold text-red-600 tabular-nums" data-testid="deplete-count">{depleteIds.size}</div></div>
          </div>

          <div className="border border-slate-200 bg-white rounded-md">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Not scanned — in inventory but not found</div>
              {missingRolls.length > 0 && (
                <button onClick={toggleAllDeplete} className="text-xs text-blue-600 hover:underline" data-testid="toggle-all-deplete">
                  {depleteIds.size === missingRolls.length ? "Untick all" : "Tick all to consume"}
                </button>
              )}
            </div>
            {missingRolls.length === 0 ? (
              <div className="p-8 text-center text-sm text-emerald-600 flex items-center justify-center gap-2"><CheckCircle weight="fill" size={18} /> Everything in inventory was scanned — nothing missing.</div>
            ) : (
              <>
                <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-800">Tick a roll to mark it <b>consumed</b> (it was likely used without recording a cut). Leave unticked to keep it — you may have just missed scanning it.{!isAdmin && <> Ticked rolls are <b>sent to an admin in Update Requests to confirm</b> before they're actually consumed.</>}</div>
                <div className="max-h-[46vh] overflow-y-auto divide-y divide-slate-100">
                  {missingRolls.map((r) => (
                    <label key={r.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer" data-testid={`missing-row-${r.id}`}>
                      <Checkbox checked={depleteIds.has(r.id)} onCheckedChange={() => toggleDeplete(r.id)} data-testid={`deplete-check-${r.id}`} />
                      {r.color_hex && <div className="w-6 h-6 border border-slate-200 rounded-sm shrink-0" style={{ background: r.color_hex }} />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{r.product_name}</div>
                        <div className="text-[11px] text-slate-500">{r.manufacturer} · {r.series} · <span className="font-mono">{r.sku}</span></div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-sm tabular-nums">{Number(r.current_length_m).toFixed(2)}m</div>
                        <div className="text-[10px] text-slate-400">{r.location || "—"} · {r.batch_number}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)} className="gap-2 rounded-md" data-testid="back-to-scan-btn"><ArrowLeft size={16} /> Back to scanning</Button>
            <Button onClick={() => setStep(2)} className="bg-slate-900 hover:bg-slate-800 gap-2 rounded-md" data-testid="to-per-unit-btn">Per-unit count <ArrowRight size={16} /></Button>
          </div>
        </div>
      )}

      {/* STEP 3 — PER-UNIT COUNT */}
      {step === 2 && (
        <div className="space-y-5" data-testid="step-per-unit">
          <div className="border border-slate-200 bg-white rounded-md">
            <div className="px-4 py-3 border-b border-slate-200 text-xs font-mono tracking-[0.15em] uppercase text-slate-600 flex items-center gap-1.5"><Cube size={14} /> Per-unit goods — count by hand</div>
            {data.per_unit.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No per-unit products in inventory.</div>
            ) : (
              <div className="max-h-[52vh] overflow-y-auto divide-y divide-slate-100">
                {data.per_unit.map((p) => {
                  const diff = perUnitDiff(p);
                  return (
                    <div key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`per-unit-row-${p.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{p.name} {p.discontinued && <span className="text-[9px] font-mono uppercase bg-slate-200 text-slate-500 rounded px-1 py-0.5 ml-1">disc</span>}</div>
                        <div className="text-[11px] text-slate-500">{p.manufacturer} · <span className="font-mono">{p.base_sku}</span> · {p.location || "—"}</div>
                      </div>
                      <div className="text-center shrink-0">
                        <div className="text-[10px] font-mono uppercase text-slate-400">System</div>
                        <div className="font-mono text-sm tabular-nums">{p.current_count}</div>
                      </div>
                      <div className="shrink-0 w-24">
                        <Label className="text-[10px] font-mono uppercase text-slate-500">Counted</Label>
                        <Input type="number" min="0" step="1" value={counts[p.id] ?? ""} onChange={(e) => setCounts((c) => ({ ...c, [p.id]: e.target.value }))} placeholder="—" className="h-9 font-mono text-center" data-testid={`count-input-${p.id}`} />
                      </div>
                      <div className="shrink-0 w-14 text-right">
                        {diff != null && diff !== 0 && (
                          <span className={`text-xs font-mono font-semibold ${diff > 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`count-diff-${p.id}`}>{diff > 0 ? "+" : ""}{diff}</span>
                        )}
                        {diff === 0 && <CheckCircle weight="fill" size={16} className="text-emerald-500 inline" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="text-[11px] text-slate-500">Leave a row blank to keep its current count. Filled rows update the system count (recorded as an audited "Stock take" adjustment).</div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="gap-2 rounded-md" data-testid="back-to-review-btn"><ArrowLeft size={16} /> Back</Button>
            <Button onClick={apply} disabled={applying} className="bg-emerald-600 hover:bg-emerald-700 gap-2 rounded-md" data-testid="finish-stock-take-btn">
              <CheckCircle size={16} /> {applying ? "Saving…" : "Finish stock take"}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 4 — SUMMARY */}
      {step === 3 && summary && (
        <div className="space-y-5" data-testid="step-summary">
          <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4 flex items-start gap-3">
            <CheckCircle weight="fill" size={22} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-emerald-900 font-medium">Stock take completed and saved to the Activity Log.</div>
              {(summary.record.pending_consume_count || 0) > 0 && (
                <div className="text-[13px] text-emerald-800 mt-1" data-testid="pending-approval-note">
                  {summary.record.pending_consume_count} not-found roll{summary.record.pending_consume_count === 1 ? "" : "s"} {summary.record.pending_consume_count === 1 ? "was" : "were"} sent to <b>Update Requests</b> for an admin to confirm before being consumed.
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-slate-200 rounded-md overflow-hidden bg-white">
            <div className="p-4 border-r border-slate-200"><div className="text-[10px] font-mono uppercase text-slate-500">Present</div><div className="font-mono text-2xl font-semibold text-emerald-600">{summary.record.present_count}</div></div>
            <div className="p-4 border-r border-slate-200"><div className="text-[10px] font-mono uppercase text-slate-500">Not scanned</div><div className="font-mono text-2xl font-semibold text-amber-600">{summary.record.missing_count}</div></div>
            <div className="p-4 border-r border-slate-200"><div className="text-[10px] font-mono uppercase text-slate-500">{(summary.record.pending_consume_count || 0) > 0 ? "Sent for approval" : "Marked consumed"}</div><div className={`font-mono text-2xl font-semibold ${(summary.record.pending_consume_count || 0) > 0 ? "text-amber-600" : "text-red-600"}`}>{(summary.record.pending_consume_count || 0) > 0 ? summary.record.pending_consume_count : summary.record.depleted_count}</div></div>
            <div className="p-4"><div className="text-[10px] font-mono uppercase text-slate-500">Counts adjusted</div><div className="font-mono text-2xl font-semibold text-blue-600">{summary.record.per_unit_adjusted_count}</div></div>
          </div>
          {summary.per_unit_adjusted?.length > 0 && (
            <div className="border border-slate-200 rounded-md">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wide text-slate-600">Per-unit adjustments</div>
              <div className="divide-y divide-slate-100">
                {summary.per_unit_adjusted.map((a) => (
                  <div key={a.product_id} className="px-4 py-2 text-sm flex items-center justify-between">
                    <span className="font-medium">{a.name}</span>
                    <span className="font-mono text-xs">{a.from} → <b>{a.to}</b> <span className={a.delta > 0 ? "text-emerald-600" : "text-red-600"}>({a.delta > 0 ? "+" : ""}{a.delta})</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-between">
            <Button variant="outline" onClick={downloadReport} className="gap-2 rounded-md" data-testid="download-report-btn"><DownloadSimple size={16} /> Download CSV report</Button>
            <Button onClick={resetAll} className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="new-stock-take-btn"><ClipboardText size={16} /> New stock take</Button>
          </div>
        </div>
      )}
    </div>
  );
}
